import type { RailLayout } from "@/data/rail-branches";
import { createFrameMemo } from "../FrameMemo/memo";
import { emergeNameOpacity, markerAppearanceT } from "../../marker/MarkerEmerge/emerge";
import { markerDepartureT } from "../../marker/MarkerDepart/depart";
import { branchAt, branchOffsetY } from "../RailBranchMotion/motion";
import { type MarkerCircleOffset } from "@/lib/timeline/scene/SceneMotion/motion";
import { departureHost, emergenceHost } from "../RailSeat/seat";
import { stayAt } from "../StayAt/stay";
import { MS_PER_PIXEL } from "@/lib/timeline/book/BookLayout";
import { flashbackSelfFade } from "../FlashbackFade/fade";
import { upcomingVehicleStay, vehicleSeat } from "../RailVehicleRow/row";
import { easedSeat, lerp, smooth } from "./easedSeat";
import { curveRideAt } from "../RailRide/ride";
import { edgeHost, mergeHostAtEnd, mergingInto, type Merge } from "../RailRide/ride";

export { stayAt };

export interface MarkerRailPose extends MarkerCircleOffset {
  scale: number;
  railDy: number;
  /** 0 to 1: a past self fades where its rail meets the present one. */
  fade?: number;
  /** Waiting ahead on the timeline, not on stage yet, so the camera does not frame it. */
  parked?: boolean;
  /** Disc paints under the person they emerge from or leave behind. */
  behind: boolean;
  nameOpacity: number;
}

export function markerRailPose(
  characterId: string,
  playheadMs: number,
  rail: RailLayout,
): MarkerRailPose | null {
  return poseMemo(rail, playheadMs, characterId, () => (
    computeRailPose(characterId, playheadMs, rail)
  ));
}

const poseMemo = createFrameMemo<RailLayout, MarkerRailPose | null>();

function computeRailPose(
  characterId: string,
  playheadMs: number,
  rail: RailLayout,
): MarkerRailPose | null {
  const stay = stayAt(rail.stays, characterId, playheadMs);
  if (stay === undefined) {
    return ridingPose(characterId, playheadMs, rail)
      ?? parkedVehiclePose(characterId, playheadMs, rail);
  }
  const branch = branchAt(rail.branches, stay.rootPlaceId, playheadMs);
  const seated = easedSeat(characterId, playheadMs, rail);
  // Someone who rode the split yarn in, or will ride it back out, is already there.
  const rodeIn = branch?.leadIn === true && branch.startMs === stay.startMs
    && edgeHost(rail, stay, stay.startMs, "start") !== null;
  const ridesOut = branch?.leadOut === true && branch.endMs === stay.endMs
    && edgeHost(rail, stay, stay.endMs, "end") !== null;
  const merge = mergingInto(rail, stay, branch, playheadMs);
  if (merge !== undefined) {
    const pose = mergingPose(merge, seated, branchOffsetY(branch, playheadMs), playheadMs, rail);
    return isSelfOf(characterId, merge.hostId) ? pose : { ...pose, behind: true };
  }
  const leaving = ridesOut ? 0 : markerDepartureT(rail.stays, characterId, stay, playheadMs);
  const arriving = rodeIn ? 1 : markerAppearanceT(
    rail.stays,
    characterId,
    stay,
    playheadMs,
    rail.minorCharacterIds,
  );
  const behind = leaving > 0
    ? departureHost(characterId, stay, rail) !== null
    : arriving < 1 && emergenceHost(characterId, stay, rail) !== null;
  const nameOpacity = leaving > 0 && behind
    ? 1 - leaving
    : emergeNameOpacity(behind ? arriving : 1);
  return {
    dx: seated.dx,
    dy: seated.dy,
    scale: 1,
    railDy: branchOffsetY(branch, playheadMs),
    behind,
    nameOpacity,
    fade: flashbackSelfFade(rail.stays, characterId, stay, playheadMs, {
      joined: rodeIn,
      rejoins: ridesOut || mergeHostAtEnd(rail, stay, branch) !== null,
    }),
  };
}

/** Down the rejoining yarn and back into the person they are, by the next scene. */
function mergingPose(
  merge: Merge,
  seated: MarkerCircleOffset,
  railDy: number,
  playheadMs: number,
  rail: RailLayout,
): MarkerRailPose {
  const host = computeRailPose(merge.hostId, playheadMs, rail);
  const t = smooth(merge.t);
  return {
    dx: lerp(seated.dx, host?.dx ?? seated.dx, t),
    dy: lerp(seated.dy, host?.dy ?? seated.dy, t),
    scale: 1,
    railDy,
    behind: t > 0.5,
    nameOpacity: 1 - t,
  };
}

/** Out of their present self and up the split yarn, or back down into them. */
function ridingPose(
  characterId: string,
  playheadMs: number,
  rail: RailLayout,
): MarkerRailPose | null {
  const ride = curveRideAt(rail, characterId, playheadMs);
  if (ride === undefined) {
    return null;
  }
  const host = computeRailPose(ride.hostId, playheadMs, rail);
  if (host === null) {
    return null;
  }
  const seatMs = playheadMs < ride.stay.startMs ? ride.stay.startMs : ride.stay.endMs - 1;
  const own = easedSeat(characterId, seatMs, rail);
  const t = smooth(ride.t);
  return {
    dx: lerp(host.dx, own.dx, t),
    dy: lerp(host.dy, own.dy, t),
    scale: 1,
    // The yarn's own height where the marker sits, so it slides along the curve.
    railDy: branchOffsetY(ride.branch, playheadMs),
    // Their own past self leads, in front. Anyone riding with them comes out from behind.
    behind: !isSelfOf(characterId, ride.hostId),
    nameOpacity: isSelfOf(characterId, ride.hostId) ? emergeNameOpacity(t) : 0,
  };
}

function isSelfOf(characterId: string, hostId: string): boolean {
  return characterId.startsWith(`${hostId}@`);
}

/** A coming vehicle stays put on the timeline, like a location, until the playhead reaches it. */
function parkedVehiclePose(
  characterId: string,
  playheadMs: number,
  rail: RailLayout,
): MarkerRailPose | null {
  const coming = upcomingVehicleStay(rail.stays, characterId, playheadMs);
  if (coming === undefined) {
    return null;
  }
  const seat = vehicleSeat(rail.stays, characterId, coming.rootPlaceId, coming.startMs);
  return {
    dx: seat.dx + (coming.startMs - playheadMs) / MS_PER_PIXEL,
    dy: seat.dy,
    scale: 1,
    railDy: branchOffsetY(
      branchAt(rail.branches, coming.rootPlaceId, coming.startMs),
      coming.startMs,
    ),
    behind: false,
    nameOpacity: 1,
    parked: true,
  };
}
